import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { fetchCompanyData } from '~/data/company';
import { Modules } from '~/data/modules';
import { useGetUserPreferencesData } from '~/data/userSettings';
import { useAppModules } from '~/scenes/AppModules/useAppModules';
import { AppDispatch } from '~/store';

export const useUserHomepageSetting = () => {
  const dispatch: AppDispatch = useDispatch();

  const {
    isModuleActive,
    isLoading: isLoadingAppModules,
    hasData: hasAppModulesData,
  } = useAppModules();

  const getInitializationStatus = useCallback((isLoading, hasData) => {
    return {
      notInitialized: !hasData && !isLoading,
      isInitializing: !hasData && isLoading,
      initialized: hasData,
    };
  }, []);

  const { modules, checkUserPreferences } = useMemo(() => {
    const modules = getInitializationStatus(
      isLoadingAppModules,
      hasAppModulesData,
    );

    return {
      modules,
      checkUserPreferences:
        modules.initialized && isModuleActive(Modules.WORKSPACE),
    };
  }, [
    isLoadingAppModules,
    hasAppModulesData,
    getInitializationStatus,
    isModuleActive,
  ]);

  useEffect(() => {
    if (modules.notInitialized) {
      dispatch(fetchCompanyData());
    }
  }, [modules, dispatch]);

  const {
    data,
    isLoading: isLoadingUserPreferences,
    hasData: hasUserPreferencesData,
  } = useGetUserPreferencesData(!checkUserPreferences);

  return useMemo(() => {
    if (checkUserPreferences) {
      const userPreferences = getInitializationStatus(
        isLoadingUserPreferences,
        hasUserPreferencesData,
      );

      const initialized = modules.initialized && userPreferences.initialized;
      const notInitialized =
        modules.notInitialized && userPreferences.notInitialized;

      return {
        useWorkspaceAsHomepage: data?.useWorkspaceAsHomepage ?? false,
        initialized,
        isInitializing: !notInitialized && !initialized,
        notInitialized,
      };
    }

    return {
      useWorkspaceAsHomepage: false,
      ...modules,
    };
  }, [
    modules,
    isLoadingUserPreferences,
    data,
    hasUserPreferencesData,
    getInitializationStatus,
    checkUserPreferences,
  ]);
};
